import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { QuestionCircleOutlined } from '@ant-design/icons';
import api from '@api/api';
import LanguageSelect from '@components/LanguageSelect/LanguageSelect';
import { regexStandardRules } from '@fe-share-utils/regexRules';
import useCustomType1Modal from '@hooks/useCustomType1Modal';
import { CommonResponseObj } from '@models/common';
import { LoginInfo, ForgetPasswordRequestInfo } from '@models/user';
import handleLoginAndSetup from '@store/thunks/sequentialThunks';
import { REMEMBER_USER_INFO } from '@utils/const';
import { Button, Checkbox, Form, Input, App, Typography, Popover } from 'antd';
import md5 from 'md5';

import type { AppDispatch } from '@store/index';

import './Login.scss';

const { Title } = Typography;

enum AuthState {
  LOGIN = 'LOGIN',
  FORGET_ENTER_USER_INFO = 'FORGET_ENTER_USER_INFO',
  FORGET_ENTER_OTP = 'FORGET_ENTER_OTP',
  FORGET_ENTER_NEW_PASSWORD = 'FORGET_ENTER_NEW_PASSWORD',
}

const AuthURLs = {
  getVerification: '/api/config-service/user/getVerifySMSCodeForChangePassword',
  submitVerification: '/api/config-service/user/verifySMSCodeForChangePassword',
  updatePassword: '/api/config-service/user/verifySMSCodeAndChangePassword',
};

const RESEND_TIME_IN_SECOND = 60;

function Login() {
  const { message } = App.useApp();
  const { t } = useTranslation();

  const [authState, setAuthState] = useState<AuthState>(AuthState.LOGIN);
  const [tempUserPayload, setTempUserPayload] =
    useState<ForgetPasswordRequestInfo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_TIME_IN_SECOND);
  const [userInfoForm] = Form.useForm();
  const [OTPForm] = Form.useForm();
  const [updatePasswordForm] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const [loggedOutPrompt] = useCustomType1Modal();

  async function onUserFormSubmit(
    formValues: LoginInfo | ForgetPasswordRequestInfo,
  ) {
    setIsSubmitting(true);

    switch (authState) {
      case AuthState.LOGIN: {
        if (!('password' in formValues) || !('remember' in formValues)) return;

        const { sellerNumber, account, password, remember } = formValues;
        try {
          await dispatch(
            handleLoginAndSetup({ sellerNumber, account, password }),
          );

          // If you reach this point, login and permission fetching were successful
          if (remember) {
            localStorage.setItem(
              REMEMBER_USER_INFO.SELLER_NUMBER,
              sellerNumber,
            );
            localStorage.setItem(REMEMBER_USER_INFO.ACCOUNT, account);
          } else {
            localStorage.removeItem(REMEMBER_USER_INFO.SELLER_NUMBER);
            localStorage.removeItem(REMEMBER_USER_INFO.ACCOUNT);
          }
        } catch (err: any) {
          message.error(err.message || t('Common.OperationFailed'));
        }
        break;
      }

      case AuthState.FORGET_ENTER_USER_INFO: {
        if (!('phoneNumber' in formValues)) return;

        try {
          const { sellerNumber, account, phoneNumber } = formValues;
          const payload = {
            sellerNumber,
            account,
            phoneNumber,
          };
          setTempUserPayload(payload);
          const res = await api.post(AuthURLs.getVerification, payload);
          const { code, msg }: CommonResponseObj = res.data;
          if (code !== 0) {
            message.error(msg);
            setIsSubmitting(false);
            return;
          }
          setAuthState(AuthState.FORGET_ENTER_OTP);

          setResendTimer(RESEND_TIME_IN_SECOND);
        } catch (err) {
          console.error(err);
        }
        setIsSubmitting(false);

        break;
      }

      default:
        console.error('Invalid auth state');
        break;
    }

    setIsSubmitting(false);
  }

  async function onOTPSubmit(formValues: { otp: string }) {
    setIsSubmitting(true);

    try {
      const payloadWithOTP = {
        ...tempUserPayload,
        idCode: formValues.otp,
      } as ForgetPasswordRequestInfo;

      const res = await api.post(AuthURLs.submitVerification, payloadWithOTP);
      const { code }: CommonResponseObj = res.data;
      if (code !== 0) {
        message.error(t('Login.OTPSubmitErrorMsg'));
        setIsSubmitting(false);
        return;
      }

      setAuthState(AuthState.FORGET_ENTER_NEW_PASSWORD);
      setTempUserPayload(payloadWithOTP);
      // 加上隱藏的 username 欄位，使密碼管理器可以成功取得
      updatePasswordForm.setFieldsValue({
        account: payloadWithOTP.account,
      });
    } catch (err) {
      console.error(err);
    }

    setIsSubmitting(false);
  }

  async function onUpdatePasswordSubmit(formValues: {
    password: string;
    repeatPassword: string;
  }) {
    setIsSubmitting(true);

    try {
      const { password, repeatPassword } = formValues;
      if (password !== repeatPassword) {
        message.error(t('Login.PasswordSubmitErrorMsg'));
        setIsSubmitting(false);
        return;
      }

      const payload = {
        ...tempUserPayload,
        hashNewPassword: md5(password),
      } as ForgetPasswordRequestInfo;

      const res = await api.post(AuthURLs.updatePassword, payload);
      const { code, msg }: CommonResponseObj = res.data;
      if (code !== 0) {
        message.error(msg);
        setIsSubmitting(false);
        return;
      }

      message.success(t('Common.OperationSuccess'));
      dispatch(
        handleLoginAndSetup({
          sellerNumber: payload.sellerNumber,
          account: payload.account,
          password,
        }),
      );
    } catch (err) {
      console.error(err);
    }

    setIsSubmitting(false);
  }

  function removeAutoLogoutFlags() {
    localStorage.removeItem('shortTermAutoLogout');
    localStorage.removeItem('longTermAutoLogout');
  }

  async function resendOTP() {
    setResendTimer(RESEND_TIME_IN_SECOND);

    try {
      const res = await api.post(AuthURLs.getVerification, tempUserPayload);
      const { code, msg }: CommonResponseObj = res.data;
      if (code !== 0) {
        message.error(msg);
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (authState !== AuthState.LOGIN) return;
    // 載入頁面時取得先前記住的帳號密碼

    userInfoForm.setFieldsValue({
      sellerNumber:
        localStorage.getItem(REMEMBER_USER_INFO.SELLER_NUMBER) ?? '',
      account: localStorage.getItem(REMEMBER_USER_INFO.ACCOUNT) ?? '',
    });
  }, [userInfoForm, authState]);

  useEffect(() => {
    if (resendTimer === -1) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  useEffect(() => {
    const shortTermAutoLogout = localStorage.getItem('shortTermAutoLogout');
    const longTermAutoLogout = localStorage.getItem('longTermAutoLogout');
    if (shortTermAutoLogout || longTermAutoLogout) {
      loggedOutPrompt({
        title: shortTermAutoLogout
          ? t('Login.IdleTwoHoursLoggedOut')
          : t('Login.SessionExpiredLoggedOut'),
        onOk: removeAutoLogoutFlags,
      });
    }
  }, []);

  return (
    <div className="Login">
      {localStorage.getItem('engMode') === 'true' ? (
        <div className="language">
          <LanguageSelect />
        </div>
      ) : null}
      <div className="Login__main">
        <div className="Login__titlePart">
          <Title className="Login__title" level={1}>
            {t('Login.Welcome')}
          </Title>
          <Title className="Login__subtitle" level={2}>
            {t('Login.StarChargerEnergyManagement')}
          </Title>
        </div>
        <div
          className={`Login__form ${
            authState === AuthState.FORGET_ENTER_USER_INFO
              ? 'Login__form--paddingBottom'
              : ''
          }`}
        >
          {/* 登入、忘記密碼：輸入用戶資訊 */}
          {(authState === AuthState.LOGIN ||
            authState === AuthState.FORGET_ENTER_USER_INFO) && (
            <>
              <Title className="Login__formTitle" level={3}>
                {authState === AuthState.LOGIN
                  ? t('Login.UserLogin')
                  : t('Login.ForgotPassword')}
              </Title>

              <Form
                initialValues={{ remember: true }}
                form={userInfoForm}
                onFinish={(formValues) => onUserFormSubmit(formValues)}
                layout="vertical"
                requiredMark={false}
              >
                <Form.Item
                  label={t('Login.SaasCompanyCode')}
                  name="sellerNumber"
                  rules={[regexStandardRules.REQUIRED(t)]}
                >
                  <Input placeholder={t('Login.EnterSaasCompanyCode')} />
                </Form.Item>
                <Form.Item
                  label={t('Login.UserAccount')}
                  name="account"
                  rules={[regexStandardRules.REQUIRED(t)]}
                >
                  <Input placeholder={t('Login.EnterUserAccount')} />
                </Form.Item>

                {authState === AuthState.LOGIN ? (
                  <Form.Item
                    label={
                      <>
                        {t('Login.Password')}
                        <Popover content={t('Login.PasswordRule')}>
                          <QuestionCircleOutlined />
                        </Popover>
                      </>
                    }
                    rules={[regexStandardRules.REQUIRED(t)]}
                    name="password"
                    className="Login__passwordInput"
                  >
                    <Input.Password placeholder={t('Login.EnterPassword')} />
                  </Form.Item>
                ) : (
                  <div
                    className={
                      authState === AuthState.FORGET_ENTER_USER_INFO
                        ? 'Login__forgotPasswordMargin'
                        : ''
                    }
                  >
                    <Form.Item
                      label={t('Login.UserPhoneNumber')}
                      name="phoneNumber"
                      rules={[regexStandardRules.JAPANESE_CELLPHONE_NUMBER(t)]}
                    >
                      <Input placeholder={t('Login.EnterUserPhoneNumber')} />
                    </Form.Item>
                  </div>
                )}

                {authState === AuthState.LOGIN && (
                  <div className="Login__actionsSection">
                    <Form.Item
                      valuePropName="checked"
                      name="remember"
                      className="Login__rememberCheckbox"
                    >
                      <Checkbox>
                        {t('Login.RememberAccount')}
                        <Popover
                          content={t('Login.ProtectAccountPrivacy')}
                          overlayStyle={{ maxWidth: '30%' }}
                        >
                          <QuestionCircleOutlined />
                        </Popover>
                      </Checkbox>
                    </Form.Item>
                    <Button
                      size="middle"
                      type="link"
                      className="Login__forgotPasswordButton"
                      onClick={() => {
                        userInfoForm.resetFields();
                        setAuthState(AuthState.FORGET_ENTER_USER_INFO);
                      }}
                    >
                      {t('Login.ForgotPassword')}
                    </Button>
                  </div>
                )}

                {authState === AuthState.LOGIN ? (
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="Login__commonButton"
                      loading={isSubmitting}
                      block
                    >
                      {t('Login.Login')}
                    </Button>
                  </Form.Item>
                ) : (
                  <div className="Login__buttonGroup">
                    <Button
                      htmlType="button"
                      className="Login__commonButton"
                      onClick={() => {
                        setAuthState(AuthState.LOGIN);
                        userInfoForm.resetFields();
                      }}
                      block
                      style={{ width: '40%' }}
                    >
                      {t('Login.ReturnLogin')}
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="Login__commonButton"
                      loading={isSubmitting}
                      block
                      style={{ width: '60%' }}
                    >
                      {t('Login.SendOTP')}
                    </Button>
                  </div>
                )}
              </Form>
            </>
          )}

          {/* 輸入 OTP 簡訊驗證碼 */}
          {authState === AuthState.FORGET_ENTER_OTP && (
            <div className="Login__OTP">
              <Title className="Login__formTitle" level={3}>
                {t('Login.ChangePasswordProcess')}
              </Title>

              <Form
                form={OTPForm}
                onFinish={(formValues) => {
                  onOTPSubmit(formValues);
                }}
                layout="vertical"
              >
                <Form.Item
                  label={t('Login.EnterSmsOTP')}
                  name="otp"
                  className="Login__OTPInput"
                >
                  <Input.OTP autoFocus length={4} />
                </Form.Item>

                <div className="Login__buttonGroup">
                  <Button
                    htmlType="button"
                    className="Login__commonButton"
                    onClick={() => resendOTP()}
                    block
                    disabled={resendTimer !== -1}
                  >
                    {t('Login.Resend')}
                    {resendTimer === -1 ? '' : ` (${resendTimer}S)`}
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="Login__commonButton"
                    loading={isSubmitting}
                    block
                  >
                    {t('Common.Determine')}
                  </Button>
                </div>
              </Form>
            </div>
          )}

          {/* 輸入新密碼 */}
          {authState === AuthState.FORGET_ENTER_NEW_PASSWORD && (
            <div className="Login__resetPassword">
              <Title className="Login__formTitle" level={3}>
                {t('Common.ChangePassword')}
              </Title>
              <Form
                form={updatePasswordForm}
                onFinish={(formValues) => onUpdatePasswordSubmit(formValues)}
                layout="vertical"
                requiredMark={false}
              >
                <Form.Item name="account" hidden>
                  <Input />
                </Form.Item>
                <Form.Item
                  label={
                    <>
                      {t('Common.NewPassword')}
                      <Popover content={t('Login.PasswordRule')}>
                        <QuestionCircleOutlined />
                      </Popover>
                    </>
                  }
                  name="password"
                  rules={[regexStandardRules.PASSWORD(t)]}
                  className="Login__passwordInput"
                >
                  <Input.Password
                    placeholder={t('Common.EnterNewPassword')}
                    autoFocus
                    autoComplete="new-password"
                  />
                </Form.Item>
                <Form.Item
                  label={t('Common.ConfirmNewPassword')}
                  name="repeatPassword"
                  rules={[regexStandardRules.PASSWORD(t)]}
                  className="Login__passwordInput--marginBottom"
                >
                  <Input.Password
                    placeholder={t('Common.ReEnterNewPassword')}
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="Login__commonButton"
                    loading={isSubmitting}
                    block
                  >
                    {t('Common.Determine')}
                  </Button>
                </Form.Item>
              </Form>
            </div>
          )}
        </div>
      </div>
      <div className="Login__footer">
        <p>copyright&copy;{new Date().getFullYear()}</p>
      </div>
    </div>
  );
}

export default Login;
